import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Offcanvas from "react-bootstrap/Offcanvas";
import {
  useGetLocationsWithAssetsQuery,
  useGetLocationsWithoutClusteringQuery,
} from "../store/slices/mapsSlice";
import MyPagination from "../Common/MyPagination";
import ContactCard from "../Contacts/ContactCard";
import { Link, useNavigate } from "react-router-dom";
import { MapFilterType } from "./MapFilters";
import ContactsList from "../Contacts/ContactsList";
import { Modal, Table } from "react-bootstrap";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import { createCS, proxyAPI } from "../proxyAPI";
import contactUUID from "../Contacts/contactUUID";
import { toast } from "react-toastify";
import { useMap } from "@vis.gl/react-google-maps";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faLocation } from "@fortawesome/free-solid-svg-icons";
import { toCamel } from "./helpers";
import ContactIcon from "../../assets/icons/contact_edit.svg2";
import AssetsIcon from "../Assets/AssetsIcon";
import AssetKind from "../Assets/AssetKind";
import EditAssetModal from "../Assets/EditAssetModal";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";

function NewGroupModal({
  contacts,
  mapFilters,
}: {
  contacts?: string[];
  mapFilters?: any;
}) {
  const navigate = useNavigate();
  const confirmDialog = useConfirmDialog();
  const [show, setShow] = useState(false);
  const [contactSet, setContactSet] = useState<string>("");

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleCreateGroup = (groupname: string) => {
    const uuid = contactUUID();
    createCS(groupname || `New group - ${uuid.slice(0, 10)}`, uuid, () => {
      setContactSet(uuid);
    });
  };

  const saveContactSet = async () => {
    let data;

    if (contacts) {
      const bodyContent = {
        contact_set: { uuid: contactSet },
        clearStatic: false,
        add: contacts,
        remove: [],
      };

      data = await proxyAPI({
        prefix: "contacts/v1",
        method: "POST",
        url: "/manage-contact-set-members",
        body: bodyContent,
      });
    }

    if (mapFilters) {
      data = await proxyAPI({
        prefix: "logic/workflows/api",
        method: "POST",
        url: `/geo/add-to-group/${contactSet}`,
        headers: { "Content-Type": "application/json" },
        body: mapFilters,
      });
    }

    if (data) {
      toast.success("Contacts added successfully");
      handleClose();
      if (
        (await confirmDialog(
          "Send broadcast to the group?",
          `Contacts added to the group successfully. Do you want to send a broadcast to this group?`
        )) === true
      ) {
        navigate("/library/broadcast/new/settings", {
          state: { contactSets: [contactSet] },
        });
      }
    }
  };

  return (
    <>
      <Button variant="colored" onClick={handleShow}>
        Add {contacts ? "selected contacts" : "all contacts"} to group
      </Button>

      <Modal
        show={show}
        onHide={handleClose}
        className="z-3000"
        backdropClassName="z-2999"
      >
        <Modal.Header closeButton>
          <Modal.Title>Modal heading</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Input
            value={[contactSet]}
            onChange={([v]: any) => {
              setContactSet(v);
            }}
            inputType="contactSet"
            contactSetType="static"
            isValidNewOption={(v) => !!v.trim()}
          />
          <JsonCollapse json={{ contactSet }} />
          <div className="d-flex justify-content-end mt-2">
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
            <Button variant="colored" onClick={saveContactSet}>
              Save Contacts in the group
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export function MapItems({
  bounds,
  filters,
  showActions = true,
  tableStyle,
  actionButtons,
}: {
  bounds: any;
  filters?: MapFilterType;
  showActions?: boolean;
  tableStyle?: React.CSSProperties;
  actionButtons?: (item: any) => React.ReactNode;
}) {
  const map = useMap();
  const [selectedContacts, setSelectedContacts] = useState([]);

  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 10,
    search: "new",
  });

  const { data = { items: [], total: 0 } } =
    useGetLocationsWithoutClusteringQuery({
      ...filters,
      Coordinates: bounds,
      Limit: pagination.perPage,
      Offset: pagination.page * pagination.perPage,
      WithContacts: true,
      Zoom: 30,
    });

  const items = data?.items || [];
  const total = data?.total || 0;

  const [targetLocation, setTargetLocation] = useState<{
    lat: number;
    lng: number;
    content: any;
  }>();

  useEffect(() => {
    if (!map || !targetLocation) return;

    const marker = new window.google.maps.Marker({
      position: targetLocation,
      map,
      title: "Selected Location",
    });
    const infoWindow = new window.google.maps.InfoWindow({
      content: `<div style="text-align: center;">
        <h4>Selected Location</h4>
        <p>Latitude: ${targetLocation.lat}</p>
        <p>Longitude: ${targetLocation.lng}</p>
      </div>`,
    });
    infoWindow.open(map, marker);
    map.panTo(targetLocation);
    return () => {
      marker.setMap(null);
      infoWindow.close();
    };
  }, [targetLocation, map]);

  return (
    <>
      {items && (
        <>
          {filters && filters.Contacts && !filters.Assets ? (
            <>
              {showActions && (
                <>
                  <NewGroupModal
                    mapFilters={{
                      ...filters,
                      Coordinates: [[bounds]],
                      WithContacts: true,
                    }}
                  />
                  {selectedContacts.length > 0 && (
                    <>
                      <NewGroupModal contacts={selectedContacts} />
                      <Link
                        to="/library/broadcast/new/settings"
                        state={{
                          contacts: selectedContacts,
                        }}
                        className="btn btn-colored"
                      >
                        Send broadcast to selected
                      </Link>
                    </>
                  )}
                </>
              )}
              <ContactsList
                contacts={items.map((i) => toCamel(i.Contact))}
                fullView={false}
                contactButton={(c: any) =>
                  showActions ? (
                    <div className="d-flex flex-nowrap">
                      <Button
                        variant="link"
                        title="Click to zoom in"
                        className="p-0 me-2"
                        onClick={() => {
                          const item = items.find(
                            (i) => i.Contact?.uuid === c.uuid
                          );
                          map?.setZoom(15);
                          map?.panTo({
                            lat: item?.Point?.coordinates[1] ?? 0,
                            lng: item?.Point?.coordinates[0] ?? 0,
                          });
                        }}
                        onMouseEnter={() => {
                          const item = items.find(
                            (i) => i.Contact?.uuid === c.uuid
                          );
                          setTargetLocation({
                            lat: item?.Point?.coordinates[1] ?? 0,
                            lng: item?.Point?.coordinates[0] ?? 0,
                            content: (
                              <ContactCard
                                contact={toCamel(item)}
                                contactFields={undefined}
                                fullView={undefined}
                                isModal={undefined}
                                onSelect={undefined}
                                showContactFields={undefined}
                                isSelected={undefined}
                                contactButton={undefined}
                              />
                            ),
                          });
                        }}
                        onMouseLeave={() => {
                          setTargetLocation(undefined);
                        }}
                      >
                        <FontAwesomeIcon icon={faLocation} />
                      </Button>
                      <Link
                        title="Send broadcast"
                        to="/library/broadcast/new/settings"
                        state={{
                          contacts: [c.uuid],
                        }}
                      >
                        <FontAwesomeIcon icon={faEnvelope} />
                      </Link>
                    </div>
                  ) : (
                    actionButtons?.(c)
                  )
                }
                allContactsButton={undefined}
                refreshList={undefined}
                notDraggable={undefined}
                isModal={undefined}
                showContactFields={undefined}
                onSelectContacts={showActions ? setSelectedContacts : undefined}
              />
            </>
          ) : (
            <div style={tableStyle ? { ...tableStyle } : {}}>
              <Table responsive>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.Uuid || i.ContactUuid || i.AssetUuid}>
                      {i.ContactUuid ? (
                        <>
                          <td className="align-middle">
                            <ContactIcon height={"1rem"} />
                            Contact
                          </td>
                          <td colSpan={4}>
                            {i.AssetUuid ? (
                              <>
                                Contact inside{" "}
                                <Link to={`/assets/assets/${i.AssetUuid}`}>
                                  asset
                                </Link>{" "}
                                location
                              </>
                            ) : (
                              ""
                            )}
                            {i.Contact && (
                              <div className="d-flex justify-content-between align-items-center">
                                <table className="table mb-0">
                                  <tbody>
                                    <ContactCard
                                      contact={toCamel(i.Contact)}
                                      contactFields={undefined}
                                      fullView={undefined}
                                      isModal={undefined}
                                      onSelect={undefined}
                                      showContactFields={undefined}
                                      isSelected={undefined}
                                      contactButton={undefined}
                                    />
                                  </tbody>
                                </table>
                                {showActions && (
                                  <div className="d-flex flex-nowrap align-items-center">
                                    <Button
                                      variant="link"
                                      title="Click to zoom in"
                                      className="p-0 me-2"
                                      onClick={() => {
                                        map?.setZoom(15);
                                        map?.panTo({
                                          lat: i?.Point?.coordinates[1] ?? 0,
                                          lng: i?.Point?.coordinates[0] ?? 0,
                                        });
                                      }}
                                      onMouseEnter={() => {
                                        setTargetLocation({
                                          lat: i?.Point?.coordinates[1] ?? 0,
                                          lng: i?.Point?.coordinates[0] ?? 0,
                                          content: (
                                            <ContactCard
                                              contact={toCamel(i.Contact)}
                                              contactFields={undefined}
                                              fullView={undefined}
                                              isModal={undefined}
                                              onSelect={undefined}
                                              showContactFields={undefined}
                                              isSelected={undefined}
                                              contactButton={undefined}
                                            />
                                          ),
                                        });
                                      }}
                                      onMouseLeave={() => {
                                        setTargetLocation(undefined);
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faLocation} />
                                    </Button>
                                    <Link
                                      title="Send broadcast"
                                      to="/library/broadcast/new/settings"
                                      state={{
                                        contacts: [i.Contact.uuid],
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faEnvelope} />
                                    </Link>
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td> {actionButtons && actionButtons(i)}</td>
                        </>
                      ) : (
                        <>
                          <td>
                            <AssetsIcon assetKind={i.Asset?.Kind} width={20} />
                            <AssetKind assetKind={i.Asset?.Kind} />
                          </td>
                          <td>
                            <EditAssetModal
                              assetUuid={i.AssetUuid || ""}
                              tab="details"
                              modalButton={
                                <span className="btn btn-link p-0 m-0">
                                  {i.Asset?.Label}
                                </span>
                              }
                            />
                          </td>

                          <td className="text-secondary text-truncate">
                            {i.Asset?.Description}
                          </td>
                          {showActions && (
                            <td className="text-end">
                              <Button
                                variant="link"
                                title="Click to zoom in"
                                className="p-0 me-2"
                                onClick={() => {
                                  map?.setZoom(15);
                                  map?.panTo({
                                    lat: i?.Point?.coordinates[1] ?? 0,
                                    lng: i?.Point?.coordinates[0] ?? 0,
                                  });
                                }}
                                onMouseEnter={() => {
                                  setTargetLocation({
                                    lat: i?.Point?.coordinates[1] ?? 0,
                                    lng: i?.Point?.coordinates[0] ?? 0,
                                    content: (
                                      <tr>
                                        {" "}
                                        <td>
                                          <AssetsIcon
                                            assetKind={i.Asset?.Kind}
                                            width={20}
                                          />
                                          <AssetKind
                                            assetKind={i.Asset?.Kind}
                                          />
                                        </td>
                                        <td>
                                          <Link
                                            to={`/assets/assets/${i.AssetUuid}`}
                                          >
                                            {i.Asset?.Label}
                                          </Link>
                                        </td>
                                        <td className="text-secondary text-truncate">
                                          {i.Asset?.Description}
                                        </td>
                                      </tr>
                                    ),
                                  });
                                }}
                                onMouseLeave={() => {
                                  setTargetLocation(undefined);
                                }}
                              >
                                <FontAwesomeIcon icon={faLocation} />
                              </Button>
                            </td>
                          )}
                          <td> {actionButtons && actionButtons(i)}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          <MyPagination
            onPaginate={(page: any, perPage: any) =>
              setPagination((p) => ({
                ...p,
                page: parseInt(page),
                perPage: parseInt(perPage),
              }))
            }
            total={total}
          />
        </>
      )}
    </>
  );
}

export function MapAssetItems({
  bounds,
  filters,
  showActions = true,
  tableStyle,
  actionButtons,
  onLoadComplete,
}: {
  bounds: any;
  filters?: MapFilterType;
  showActions?: boolean;
  tableStyle?: React.CSSProperties;
  actionButtons?: (item: any) => React.ReactNode;
  onLoadComplete?: (data: { items: any[]; total: number }) => void;
}) {
  const map = useMap();

  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 10,
    search: "new",
  });

  const { data = { items: [], total: 0 } } = useGetLocationsWithAssetsQuery({
    ...filters,
    Coordinates: bounds,
    Limit: pagination.perPage,
    Offset: pagination.page * pagination.perPage,
    WithContacts: true,
    Zoom: 30,
  });

  useEffect(() => {
    if (onLoadComplete && data) {
      onLoadComplete(data);
    }
  }, [data, onLoadComplete]);

  const items = data?.items || [];
  const total = data?.total || 0;

  const [targetLocation, setTargetLocation] = useState<{
    lat: number;
    lng: number;
    content: any;
  }>();

  useEffect(() => {
    if (!map || !targetLocation) return;

    const marker = new window.google.maps.Marker({
      position: targetLocation,
      map,
      title: "Selected Location",
    });
    const infoWindow = new window.google.maps.InfoWindow({
      content: `<div style="text-align: center;">
        <h4>Selected Location</h4>
        <p>Latitude: ${targetLocation.lat}</p>
        <p>Longitude: ${targetLocation.lng}</p>
      </div>`,
    });
    infoWindow.open(map, marker);
    map.panTo(targetLocation);
    return () => {
      marker.setMap(null);
      infoWindow.close();
    };
  }, [targetLocation, map]);

  return (
    <>
      {items?.length ? (
        <>
          <div style={tableStyle ? { ...tableStyle } : {}}>
            <Table responsive>
              <tbody>
                {items.map((i) => (
                  <tr key={i.Uuid}>
                    <>
                      <td>
                        <AssetsIcon assetKind={i?.Kind} width={20} />
                        <AssetKind assetKind={i?.Kind} />
                      </td>
                      <td>
                        <EditAssetModal
                          assetUuid={i?.Uuid || ""}
                          tab="details"
                          modalButton={
                            <span className="btn btn-link p-0 m-0">
                              {i?.Label}
                            </span>
                          }
                        />
                      </td>

                      <td className="text-secondary text-truncate">
                        {i?.Description}
                      </td>
                      {showActions && (
                        <td className="text-end">
                          <Button
                            variant="link"
                            title="Click to zoom in"
                            className="p-0 me-2"
                            onClick={() => {
                              map?.setZoom(15);
                              map?.panTo({
                                lat: i?.Point?.coordinates[1] ?? 0,
                                lng: i?.Point?.coordinates[0] ?? 0,
                              });
                            }}
                            onMouseEnter={() => {
                              setTargetLocation({
                                lat: i?.Point?.coordinates[1] ?? 0,
                                lng: i?.Point?.coordinates[0] ?? 0,
                                content: (
                                  <tr>
                                    {" "}
                                    <td>
                                      <AssetsIcon
                                        assetKind={i?.Kind}
                                        width={20}
                                      />
                                      <AssetKind assetKind={i?.Kind} />
                                    </td>
                                    <td>
                                      <Link to={`/assets/assets/${i?.Uuid}`}>
                                        {i?.Label}
                                      </Link>
                                    </td>
                                    <td className="text-secondary text-truncate">
                                      {i?.Description}
                                    </td>
                                  </tr>
                                ),
                              });
                            }}
                            onMouseLeave={() => {
                              setTargetLocation(undefined);
                            }}
                          >
                            <FontAwesomeIcon icon={faLocation} />
                          </Button>
                        </td>
                      )}
                      <td> {actionButtons && actionButtons(i)}</td>
                    </>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <MyPagination
            onPaginate={(page: any, perPage: any) =>
              setPagination((p) => ({
                ...p,
                page: parseInt(page),
                perPage: parseInt(perPage),
              }))
            }
            total={total}
          />
        </>
      ) : (
        <div className="alert alert-secondary">
          No assets found matching the criteria.
        </div>
      )}
    </>
  );
}

export default function MapSidebar({
  bounds,
  filters,
  show,
  setShow,
}: {
  bounds: any;
  filters?: MapFilterType;
  show: boolean;
  setShow: (show: boolean) => void;
}) {
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  return (
    <>
      <Button
        variant="link"
        onClick={handleShow}
        className="p-0 px-2 mx-2 align-baseline"
      >
        See items
      </Button>

      <Offcanvas
        show={show}
        onHide={handleClose}
        placement="end"
        style={{ width: "600px" }}
      >
        <Offcanvas.Header closeButton className="bg-colored-reverse text-white">
          <Offcanvas.Title className="fw-bold">Map Items</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="bg-page">
          <MapItems bounds={bounds} filters={filters} />
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}
